import {
	CapabilitiesEnum,
	TenantGroupEnum,
	TenantTypeEnum
}                                                 from '@app/modules/admin-tenant/models/tenant.model';
import { ArchivingExportDateType }                from '@app/modules/archiving/models/archiving-common.model';
import { BusinessPremises }                       from '@app/modules/business-premises/models/business-premises.model';
import {
	ArchivingContractTypeEnum,
	ContractProductPropertiesLogTypes,
	ContractProductTypes,
	ContractTypeGroupEnum
}                                                 from '@app/modules/contract/models/contract.model';
import { FccVersionStatusEnum }                   from '@app/modules/fcc-version/models/fcc-version.model';
import { TssPreInitializationModeEnum }           from '@app/modules/tss-pre-initialization/models/tss-pre-initialization.model';
import { RoleClassification }                     from '@appConfig/authorization-config';
import { RequestState }                           from '@core/interfaces/api.interface';
import { ENTITY_TYPE }                            from '@core/interfaces/entity.interface';
import { SelectVersionModalComparisonResultEnum } from '@core/modals/select-version-modal/select-version-modal.config';
import {
	ApplicationScopeTypes,
	ScopePermissionCheckEnum
}                                                 from '@models/scope.enums';
import { ManagedObjectTypeEnum }                  from '@shared/models/managed-object.model';
import { RoleTypes }                              from '@shared/models/roles.model';

export interface IFormPasswordPolicyItem {
	value: number;

	description: string;
}


export interface IFormPasswordPolicy {
	minLength: IFormPasswordPolicyItem;

	digits: IFormPasswordPolicyItem;

	upperChars: IFormPasswordPolicyItem;

	lowerChars: IFormPasswordPolicyItem;

	specialChars: IFormPasswordPolicyItem;
}


export enum FormDataTreatmentEnum {
	BOOLEAN_TO_I18N = 'booleanToI18n',
	// BP_ID_TO_NAME = 'businessPremisesIdToI18n',
	DATE_TO_LOCALE_STRING = 'dateToString',
	COUNTRY_CODE_TO_I18N = 'countryCodeToI18n',
	EMPTY_PARENT_ORG_ID_TO_ROOT_LEVEL = 'emptyParentOrgIdToRootLevel',
	ERS_DEVICE_TYPE_TO_I18N = 'ersDeviceTypeToI18n',
	HAS_PERMISSION = 'hasPermission',
	HIDE_CONDITIONALLY = 'hideConditionally',
	I18N_TO_COUNTRY_CODE = 'i18nToCountryCode',
	LEGAL_FORM_TO_I18N = 'legalFormToI18n',
	ORG_ID_TO_NAME = 'organizationIdToName',
	SALUTATION_TO_I18N = 'salutationToI18n',
	STATUS_TO_I18N = 'statusToI18n',
	STRING_TO_DATE = 'stringToDate',
	TENANT_TYPE_TO_I18N = 'tenantTypeToI18n'
}

export interface IFormSelectItemTooltipItem {
	value: string;
	styleClass?: string;
}


export interface IFormSelectItemGroupProps {
	groupType?: string;
	/**
	 * Often, the value currently assigned to the entity has/gets groupId 1
	 * (corresponding to groupType 'assignedGroupLabel'),
	 * potentially selectable values have/get groupId 2  (corresponding to
	 * groupType 'searchGroupLabel'),
	 * whereas groupId -1 is used in UPDATE forms only: if the entity has a
	 * pre-assigned value, this value gets groupId 1, and a second, empty list
	 * item gets groupId -1 (corresponding to groupType 'searchGroupMoreLabel'),
	 * and no search request is done (then, the user should use the search
	 * field (input element) to get a list to select from)
	 */
	groupId?: number;
}

export interface IFormSelectItem extends IFormSelectItemGroupProps {
	[ key: string ]: any;

	disabled?: boolean;
	label?: string;
	loading?: boolean;
	required?: boolean;
	tooltipDataList?: IFormSelectItemTooltipItem[];
}

export interface IFormGenericSelectItem<ValueType> extends IFormSelectItem {
	value: ValueType;
}

export interface IFormBusinessPremises extends IFormSelectItem {
	businessPremises: BusinessPremises;
}


export interface IFormRole extends IFormSelectItem {
	roleId: RoleTypes;

	classificationId: RoleClassification;

	name: string;

	type: string;
}

export interface IFormTenantType extends IFormSelectItem {
	value: TenantTypeEnum;
}

export interface IFormTenantContractLog extends IFormSelectItem {
	contract: ContractProductTypes;
	currentContractPropertiesLog: ContractProductPropertiesLogTypes;
	value: any;
	label: string;
}

export interface IFormTenantGroup extends IFormSelectItem {
	value: TenantGroupEnum;
}

export interface IFormCapability extends IFormSelectItem {
	value: CapabilitiesEnum;
}

export interface IFormArchivingDateType extends IFormSelectItem {
	value: ArchivingExportDateType;
}

export interface IFormContractTypeGroup extends IFormSelectItem {
	value: ContractTypeGroupEnum;
}

export interface IFormContract extends IFormSelectItem {
	contract: ContractProductTypes;
}

export interface IFormFccVersionStatusType extends IFormSelectItem {
	value: FccVersionStatusEnum;
}

export interface IFormArchivingContractType extends IFormSelectItem {
	value: ArchivingContractTypeEnum;
}

export interface IFormResult<T> {
	action: FORM_ACTION | 'delete'; // TODO maybe we need to change this to entity Actions, delete is for entity only not connected with forms
	requestState: RequestState;

	hideForm: boolean;

	entity?: T;
}

export interface IBaseFormManagedObjectType {
	id: ManagedObjectTypeEnum;

	name: string;
}

export interface IClientFormManagedObjectType extends IBaseFormManagedObjectType {}

export interface IUserFormManagedObjectType extends IBaseFormManagedObjectType {
	showOnRoles: RoleTypes[];
}

export interface IFormTssPreInitializationType {
	label: string;

	value: TssPreInitializationModeEnum;
}

export enum FormElementEnum {
	DATE_PICKER = 'datepicker',
	DATE_RANGE_PICKER = 'dateRangePicker',
	INPUT = 'input',
	SELECT = 'select',
	SWITCH = 'switch',
	TEXT_AREA = 'textArea'
}


export enum FormModeEnum {
	TenantCreateWithoutUser = 'tenantCreateWithoutUser',
	TenantCreateSubaccount = 'tenantCreateSubaccount',
	TenantInitialize = 'tenantInitialize',
	TenantUpdate = 'tenantUpdate',
}

export enum FormTenantCreationUserModeEnum {
	WithUser = 'withUser',
	WithoutUser = 'withoutUser'
}


export enum FORM_ACTION {
	ABORT = 'abort',
	ALREADY_DELETED = 'already deleted',
	ALREADY_EXISTS = 'already exist',
	CREATE = 'create',
	UPDATE = 'update'
}

export enum FormRequestFinishedActionEnum {
	NAVIGATE = 'navigate',
	EMIT_FORM_RESULT = 'emit form result',
}

export enum FormSubmitActionEnum {
	EMIT_FORM_VALUES = 'emitFormValues',
	REQUEST_AND_EMIT_RESULT = 'requestAndEmitResult',
	REQUEST_AND_NAVIGATE = 'requestAndNavigate'
}

export interface IFormElementConfig {
	elementType?: FormElementEnum;

	type?: string;

	tabindex?: string;

	required?: boolean;

	hideLabel?: boolean;

	hideError?: boolean;

	hideErrorDescriptions?: boolean;

	hidePlaceholder?: boolean;

	showInfo?: boolean;

	maxLength?: number;
}

export interface IFormData extends IFormElementConfig {
	addSpaceToPrevious?: boolean;
	comparisonResult?: SelectVersionModalComparisonResultEnum;
	dbValue?: string;
	disabled?: boolean;
	elsterField?: boolean;
	elsterRequiredCondition?: ( key: string, value: any ) => boolean,
	elsterRequiredField?: boolean;
	elsterShowIndicator?: boolean;
	/** Holds the key the IFormData object has in the IFormBaseData object */
	formControlName?: string;
	hide: boolean;
	/**
	 * A function defining one or more conditions under which `IFormData.hide`
	 * has to be set to `true`.
	 * Use in combination with `FormDataTreatmentEnum.HIDE_CONDITIONALLY`
	 */
	hideConditionally?: ( formItem: IFormData, localVersion: ENTITY_TYPE, dbVersion: ENTITY_TYPE ) => boolean;
	i18nPath: string;
	localValue?: string;
	objectKeyPath?: string;
	order: number;
	overwriteValue?: string;
	/* Use in combination with `FormDataTreatmentEnum.HAS_PERMISSION` */
	permissionData?: IFormPermissionData;
	specialTreatments: FormDataTreatmentEnum[];
}

export interface IFormBaseData {
	[ key: string ]: IFormData;
}

export interface IFormCreateData {
	key: string;

	val: any;
}

export interface IFormPermissionData {
	scopes: ApplicationScopeTypes | ApplicationScopeTypes[];
	permissionCheck: ScopePermissionCheckEnum;
}

export interface IFormUpdateData {
	key: string;

	fallbackValue: any;
}

export interface IForm {
	baseData: IFormBaseData;
	createData: IFormCreateData[];
	updateData: IFormUpdateData[];
	preserveData: IFormUpdateData[];
}

export enum ActiveFormType {
	ADMIN_TENANT = 'adminTenant',
	ARCHIVING = 'archiving',
	BUSINESS_PREMISES = 'businessPremises',
	CLIENT = 'client',
	CONTRACT = 'contract',
	ENCRYPTION_KEY = 'encryptionKey',
	ERS = 'ers',
	EXPORT_CONFIG = 'export',
	FCC_VERSION = 'fccVersion',
	KEYWORD = 'keyword',
	ORGANIZATION = 'organization',
	OPEN_ARCHIVE = 'openArchive',
	OUTAGE = 'outage',
	TSS = 'tss',
	TSS_PRE_INITIALIZATION = 'tssPreInitialization',
	USER = 'user'
}

export interface ActiveForm {
	entityId: string;
}

export interface ActiveForms {
	businessPremises: ActiveForm;

	ers: ActiveForm;

	organizations: ActiveForm;

	users: ActiveForm;
}

export enum UiSwitchSizeEnum {
	SMALL = 'small',
	MEDIUM = 'medium',
	LARGE = 'large',
}

